import { show } from "@intercom/messenger-js-sdk";

// Set only while <ExpertAgentWidget /> is mounted. Intercom never boots beside
// it, so this is the page's only chat.
let openExpertAgent: (() => void) | null = null;

/** Returns the unregister function, for use as an effect cleanup. */
export function registerExpertAgentOpener(open: () => void): () => void {
  openExpertAgent = open;
  return () => {
    if (openExpertAgent === open) {
      openExpertAgent = null;
    }
  };
}

// True only if something outside this module booted the Intercom SDK.
function isIntercomBooted(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as { Intercom?: unknown }).Intercom === "function"
  );
}

export function isSupportChatAvailable(): boolean {
  return openExpertAgent !== null || isIntercomBooted();
}

/**
 * The help page, with the question prefilled. Where no chat is mounted this is
 * still Sophia, one page away, rather than an email nobody can see arrive.
 */
export function getSupportHref(message?: string): string {
  return message ? `/help?message=${encodeURIComponent(message)}` : "/help";
}

/**
 * Opens the support chat mounted on this page: Sophia or the Intercom
 * messenger. Deliberately no prefill: showNewMessage forces a brand-new
 * conversation, kicking users out of an ongoing support thread. Returns false
 * when neither is on the page so callers can fall back to `getSupportHref()`.
 */
export function openSupportChat(): boolean {
  // Sophia first: Intercom's shutdown() leaves window.Intercom defined, so it
  // can still look booted after Sophia has taken its place.
  if (openExpertAgent) {
    openExpertAgent();
    return true;
  }
  if (!isIntercomBooted()) {
    return false;
  }
  show();
  return true;
}

/**
 * Imperative variant for non-anchor surfaces (toast actions, buttons).
 * Anchors should render `getSupportHref()` as a real href instead so
 * middle-click and no-JS keep working.
 */
export function openSupportChatOrHelp(message?: string): void {
  if (!openSupportChat()) {
    window.location.href = getSupportHref(message);
  }
}
